import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2, AlertCircle, Lock, FileSpreadsheet } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { formatEuro, formatDate } from '@/lib/format';
import { getBudgetStatus, getProjectBudget, syncProjectBudget } from '@/lib/costControl/service';

export const CC_IDS = {
  page: 'cost-control-page', status: 'budget-status', pending: 'budget-pending', restricted: 'budget-restricted',
  syncButton: 'budget-sync-button', table: 'budget-table', row: 'budget-row', totals: 'budget-totals', filter: 'budget-filter', error: 'budget-error',
};

const CATS = [['budget_labour', 'Labour'], ['budget_materials', 'Materials'], ['budget_subcontractor', 'Subcontractor'], ['budget_plant', 'Plant'], ['budget_other', 'Other']];

async function fetchProject([_, id, organisationId]) {
  let q = supabase.from('projects').select('*').eq('id', id);
  if (organisationId) q = q.eq('organisation_id', organisationId);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return data;
}

export default function ProjectCostControl() {
  const { id: projectId } = useParams();
  const { organisationId, role } = useAuth();
  const canView = ['owner', 'admin', 'manager'].includes(role || '');
  const isAdmin = ['owner', 'admin'].includes(role || '');
  const [filter, setFilter] = useState('');
  const [syncing, setSyncing] = useState(false);

  const { data: project } = useSWR(projectId ? ['project', projectId, organisationId] : null, fetchProject);
  const { data: status, error: statusError, mutate: mutateStatus } = useSWR(canView && projectId ? ['budget-status', projectId] : null, () => getBudgetStatus(projectId));
  const { data: rows, error: rowsError, isLoading, mutate: mutateRows } = useSWR(canView && projectId ? ['project-budget', projectId] : null, () => getProjectBudget(projectId));

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (rows || []).filter((r) => !f || [r.cost_codes?.code, r.cost_codes?.description, r.trade].some((v) => (v || '').toLowerCase().includes(f)));
  }, [rows, filter]);
  const totals = useMemo(() => {
    const t = { budget_total: 0 }; CATS.forEach(([k]) => { t[k] = 0; });
    for (const r of filtered) { t.budget_total += Number(r.budget_total); CATS.forEach(([k]) => { t[k] += Number(r[k]); }); }
    return t;
  }, [filtered]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncProjectBudget(projectId);
      toast.success(res.status === 'ready' ? (res.already_synced ? `Already up to date (revision ${res.revision_number})` : `Budget revision ${res.revision_number}${res.is_baseline ? ' (baseline)' : ''} created from Internal Cost Budget v${res.source_version} · ${res.cost_codes} cost codes`) : 'No confirmed Internal Cost Budget yet');
      await Promise.all([mutateStatus(), mutateRows()]);
    } catch (e) { toast.error(e.message || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const err = statusError || rowsError;
  const pending = status && status.budget_status === 'budget_pending';
  const restricted = !canView || status?.budget_status === 'restricted';

  return (
    <div data-testid={CC_IDS.page} className="p-6 md:p-8 max-w-7xl mx-auto">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal mb-4"><ArrowLeft size={14} /> Back to project</Link>
      <PageHeader title="Cost Control — Budget" subtitle={project ? `${project.name || 'Project'} · ${project.project_code || ''}` : 'Project budget by cost code'}
        actions={canView && isAdmin && (
          <Button type="button" variant="outline" size="sm" data-testid={CC_IDS.syncButton} disabled={syncing} onClick={handleSync}>
            {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Sync from Internal Cost Budget
          </Button>
        )} />

      {restricted ? (
        <div data-testid={CC_IDS.restricted} className="rounded-lg border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">
          <Lock size={20} className="mx-auto mb-2 text-bronze" /> Project budgets are restricted to owner, admin and manager roles.
        </div>
      ) : err ? (
        <div data-testid={CC_IDS.error} className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <div>{err.message}{/relation|does not exist|schema cache/i.test(err.message || '') && <div className="mt-1 text-xs">Cost Control tables are not present yet — run <code>cc1_cost_codes_migration.sql</code>.</div>}</div>
        </div>
      ) : isLoading || !status ? (
        <div className="py-16 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Loading budget…</div>
      ) : pending ? (
        <div data-testid={CC_IDS.pending} className="rounded-lg border border-dashed border-border bg-muted/30 px-5 py-12 text-center">
          <FileSpreadsheet size={22} className="mx-auto mb-2 text-bronze" />
          <div className="font-display font-semibold text-lg text-charcoal">Budget Pending</div>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">The original construction budget comes only from a confirmed <strong>Internal Cost Budget</strong>. BOQ selling values are never used as cost.</p>
          <Link to={`/projects/${projectId}/estimating`} className="inline-block mt-4 text-sm text-bronze hover:underline">Go to Estimating to import the Internal Cost Budget →</Link>
        </div>
      ) : (
        <>
          <div data-testid={CC_IDS.status} className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Tile label="Original budget (baseline)" value={formatEuro(status.baseline_budget)} sub={`Revision ${status.baseline_revision} · set ${formatDate(status.baseline_at)} · permanent`} />
            <Tile label="Current budget revision" value={formatEuro(status.current_budget)} sub={`Revision ${status.current_revision} · from Internal Cost Budget v${status.source_version} · ${status.cost_code_count} cost codes`} />
            <Tile label="Revised budget" value={formatEuro(status.current_budget)} sub="Approved variations: none yet (CC-5)" />
            <Tile label="Sync state" value={status.sync_required ? 'Sync required' : 'Up to date'} sub={status.sync_required ? 'A newer confirmed budget exists' : 'Snapshot matches confirmed version'} tone={status.sync_required ? 'text-amber-700' : 'text-emerald-700'} small />
          </div>
          {status.sync_required && isAdmin && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 flex items-center justify-between gap-3">
              A newer Internal Cost Budget has been confirmed. <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing}>Sync now</Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 mb-3">
            <Input data-testid={CC_IDS.filter} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by code, description or trade…" className="h-10 max-w-sm" />
            <div className="text-xs text-muted-foreground tabular-nums">{filtered.length} cost codes</div>
          </div>
          <div data-testid={CC_IDS.table} className="rounded-lg border border-border bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs">Cost code</TableHead><TableHead className="text-xs">Description</TableHead><TableHead className="text-xs">Trade</TableHead>
                {CATS.map(([k, l]) => <TableHead key={k} className="text-xs text-right">{l}</TableHead>)}
                <TableHead className="text-xs text-right">Budget (current revision)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={CC_IDS.row}>
                    <TableCell className="text-sm font-medium">{r.cost_codes?.code}</TableCell>
                    <TableCell className="text-sm">{r.cost_codes?.description}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.trade}</TableCell>
                    {CATS.map(([k]) => <TableCell key={k} className="text-sm text-right tabular-nums">{Number(r[k]) ? formatEuro(r[k]) : <span className="text-muted-foreground">—</span>}</TableCell>)}
                    <TableCell className="text-sm text-right tabular-nums font-semibold">{formatEuro(r.budget_total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow data-testid={CC_IDS.totals} className="bg-muted/40 font-semibold">
                  <TableCell colSpan={3} className="text-sm">Total</TableCell>
                  {CATS.map(([k]) => <TableCell key={k} className="text-sm text-right tabular-nums">{formatEuro(totals[k])}</TableCell>)}
                  <TableCell className="text-sm text-right tabular-nums">{formatEuro(totals.budget_total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">Committed, actual, estimated cost to complete and forecast final cost columns arrive with the next Cost Control phases. <Badge className="ml-1 bg-muted text-muted-foreground border-0">CC-1</Badge></div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub, tone = 'text-charcoal', small }) {
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display font-bold ${small ? 'text-base' : 'text-xl'} tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
